import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useBusiness } from "@/contexts/BusinessContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const SubscriptionContext = createContext();

export const SubscriptionProvider = ({ children }) => {
  const { selectedBusiness } = useBusiness();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSubscription = useCallback(async (businessId) => {
    if (!businessId) {
      setSubscriptionData(null);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/subscriptions/my-subscription?business_id=${businessId}`,
        { credentials: "include" }
      );
      if (res.ok) {
        setSubscriptionData(await res.json());
      } else {
        setSubscriptionData(null);
      }
    } catch {
      setSubscriptionData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscription(selectedBusiness?.business_id);
  }, [selectedBusiness?.business_id, fetchSubscription]);

  const subscription = subscriptionData?.subscription || null;
  const plan = subscriptionData?.plan || null;
  const status = subscriptionData?.status || "none";
  const moduleAccess = subscriptionData?.module_access || {};
  const inGracePeriod = subscriptionData?.in_grace_period || false;
  const graceDaysLeft = subscriptionData?.grace_days_left || 0;
  const daysUntilExpiry = subscriptionData?.days_until_expiry;

  const hasModuleAccess = useCallback((moduleKey) => {
    // No subscription = unrestricted (free tier or not yet set up)
    if (!subscription) return true;
    // Super admin bypass not needed here — handled at component level
    if (status === "suspended") return false;
    if (status === "expired" && !inGracePeriod) return false;
    // Check module access from plan
    if (Object.keys(moduleAccess).length === 0) return true;
    return moduleAccess[moduleKey] !== false;
  }, [subscription, status, inGracePeriod, moduleAccess]);

  return (
    <SubscriptionContext.Provider value={{
      subscription, plan, status, moduleAccess, loading,
      inGracePeriod, graceDaysLeft, daysUntilExpiry,
      hasModuleAccess, refetch: () => fetchSubscription(selectedBusiness?.business_id),
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
};

export const useSubscription = () => {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error("useSubscription must be used within a SubscriptionProvider");
  }
  return context;
};

export default SubscriptionContext;
